import type { MaterialContext, ScenePreset } from "@cfg/brand-sdk";

/**
 * Moulded ABS, after the LEGO Movie bricks: a mirror-sharp clearcoat over a
 * slightly blurrier dielectric base, a touch of light wrap standing in for
 * subsurface glow, and handling wear that only shows in reflections.
 *
 * The wear is roughness-only (never colour), sampled as solid noise at the
 * world position in mm since kernel meshes carry no UVs:
 * - smudges: sparse soft patches that haze the coat;
 * - fingerprints: fine curved ridges inside some smudges;
 * - scratches: rare, thin, nearly straight hairlines.
 * Fine detail fades to its average once sub-pixel so distant views stay clean.
 * Needs an environment map (see the preset's lightformers) to read as plastic.
 */
export interface AbsOptions {
  color: string;
  /** Overall amount of smudges, prints and scratches, 0..1. */
  wear?: number;
  /** Light wrap for a soft subsurface glow, 0..0.5. */
  wrap?: number;
  /** Base-layer roughness under the clearcoat. */
  roughness?: number;
  /** Clearcoat roughness where the surface is clean. */
  coatRoughness?: number;
}

const pars = /* glsl */ `
varying vec3 vAbsPos;
uniform float uWear;
uniform float uWrap;

vec3 absHash(vec3 p) {
  p = vec3(dot(p, vec3(127.1, 311.7, 74.7)), dot(p, vec3(269.5, 183.3, 246.1)), dot(p, vec3(113.5, 271.9, 124.6)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}

// 3D gradient noise, roughly -0.7..0.7
float absNoise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(dot(absHash(i), f), dot(absHash(i + vec3(1, 0, 0)), f - vec3(1, 0, 0)), u.x),
        mix(dot(absHash(i + vec3(0, 1, 0)), f - vec3(0, 1, 0)), dot(absHash(i + vec3(1, 1, 0)), f - vec3(1, 1, 0)), u.x), u.y),
    mix(mix(dot(absHash(i + vec3(0, 0, 1)), f - vec3(0, 0, 1)), dot(absHash(i + vec3(1, 0, 1)), f - vec3(1, 0, 1)), u.x),
        mix(dot(absHash(i + vec3(0, 1, 1)), f - vec3(0, 1, 1)), dot(absHash(i + vec3(1, 1, 1)), f - vec3(1, 1, 1)), u.x), u.y),
    u.z);
}

// x: coat haze 0..1, y: base haze 0..1.
vec2 absWear(vec3 p, float px) {
  // Smudges: sparse soft patches, ~6 mm.
  float smudge = smoothstep(0.12, 0.45, absNoise(p / 6.0) + 0.35 * absNoise(p / 2.2 + 7.0));

  // Fingerprints: curved ridges ~0.35 mm apart inside the stronger patches.
  float printMask = smoothstep(0.3, 0.55, absNoise(p / 4.0 + 31.0)) * smudge;
  float ridgeFade = 1.0 - smoothstep(0.08, 0.2, px);
  float ridges = 0.5 + 0.5 * sin(absNoise(p / 3.5 + 13.0) * 55.0 + absNoise(p / 1.1 + 3.0) * 6.0);
  float prints = printMask * mix(0.5, ridges, ridgeFade);

  // Scratches: noise squashed along a slowly varying direction gives long thin
  // ridges; a sparse gate keeps only a few hairlines.
  vec3 d = normalize(vec3(absNoise(p / 20.0 + 1.0), absNoise(p / 20.0 + 5.0), absNoise(p / 20.0 + 9.0)) + vec3(1e-4));
  vec3 q = p / 0.9;
  q -= 0.96 * d * dot(q, d);
  float line = 1.0 - abs(absNoise(q + 51.0));
  line = pow(line, 40.0);
  float gate = smoothstep(0.35, 0.55, absNoise(p / 5.0 + 71.0));
  float scratchFade = 1.0 - smoothstep(0.03, 0.12, px);
  float scratches = line * gate * scratchFade + 0.02 * gate * (1.0 - scratchFade);

  float coat = uWear * clamp(0.55 * smudge + 0.6 * prints + 0.9 * scratches, 0.0, 1.0);
  float base = uWear * clamp(0.25 * smudge + 0.5 * scratches, 0.0, 1.0);
  return vec2(coat, base);
}
`;

export function absMaterial(three: MaterialContext["three"], opts: AbsOptions): ReturnType<ScenePreset["material"]> {
  const { wear = 1, wrap = 0.18, roughness = 0.28, coatRoughness = 0.04 } = opts;

  const m = new three.MeshPhysicalMaterial({
    color: new three.Color(opts.color),
    roughness,
    metalness: 0,
    ior: 1.53,
    specularIntensity: 1,
    clearcoat: 1,
    clearcoatRoughness: coatRoughness,
  });

  // Wrapped N.L for direct lights: a soft glow past the terminator, like light
  // scattering just under the surface of pigmented ABS.
  const lightsPars = three.ShaderChunk.lights_physical_pars_fragment.replace(
    "float dotNL = saturate( dot( geometryNormal, directLight.direction ) );",
    "float dotNL = saturate( ( dot( geometryNormal, directLight.direction ) + uWrap ) / ( 1.0 + uWrap ) );",
  );

  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, {
      uWear: { value: wear },
      uWrap: { value: wrap },
    });
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vAbsPos;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvAbsPos = (modelMatrix * vec4(transformed, 1.0)).xyz;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${pars}`)
      .replace("#include <lights_physical_pars_fragment>", lightsPars)
      .replace(
        "#include <clipping_planes_fragment>",
        `#include <clipping_planes_fragment>
        vec2 absW = absWear(vAbsPos, length(fwidth(vAbsPos)));`,
      )
      .replace(
        "#include <roughnessmap_fragment>",
        `#include <roughnessmap_fragment>
        roughnessFactor = mix(roughnessFactor, 0.55, absW.y);`,
      )
      .replace(
        "#include <lights_physical_fragment>",
        `#include <lights_physical_fragment>
        #ifdef USE_CLEARCOAT
          material.clearcoatRoughness = mix(material.clearcoatRoughness, 0.32, absW.x);
        #endif`,
      );
  };
  m.customProgramCacheKey = () => "cfg-abs-v1";
  return m;
}
